import { useRef, useCallback } from 'react';
import type { GeneralLedgerListItem } from '../types';

/**
 * Custom hook for caching item heights in virtualized lists
 * Provides efficient height caching with automatic cache invalidation
 */
export function useHeightCache(items: GeneralLedgerListItem[]) {
  const heightCacheRef = useRef<Map<string, number>>(new Map());
  const previousItemsRef = useRef<GeneralLedgerListItem[]>([]);

  const getItemSize = useCallback(
    (index: number): number => {
      const item = items[index];
      if (!item) return 0;

      // Use cached height if available
      const cacheKey = item.key;
      const cachedHeight = heightCacheRef.current.get(cacheKey);
      if (cachedHeight !== undefined) {
        return cachedHeight;
      }

      // Calculate and cache the height
      const height = item.ItemComponent.getHeight(item.itemProps);
      heightCacheRef.current.set(cacheKey, height);
      return height;
    },
    [items],
  );

  const clearCache = useCallback(() => {
    heightCacheRef.current.clear();
  }, []);

  const shouldClearCache = useCallback((): boolean => {
    // Check if items have structurally changed
    const hasDataChanged =
      items.length !== previousItemsRef.current.length ||
      items.some((item, index) => item.key !== previousItemsRef.current[index]?.key);

    if (hasDataChanged) {
      previousItemsRef.current = items;
      return true;
    }

    return false;
  }, [items]);

  return {
    getItemSize,
    clearCache,
    shouldClearCache,
  };
}
